/**
 * Scroll area with infinite-scroll pagination for the Sources list.
 *
 * Adapted from `apps/aegix-aim-react/src/components/settings/SettingsScrollArea.tsx`
 * but without the AIM-specific `PaginationScroll` (SimpleBar) dep — uses a
 * plain scroll listener instead. Functionally equivalent for the Sources page.
 */
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useRef, useEffect } from 'react';

export interface SourcesScrollAreaProps {
  children: React.ReactNode;
  isLoaded?: boolean;
  isLoading?: boolean;
  offset?: number;
  limit?: number;
  setOffset?: (offset: number) => void;
  nextUrl?: string | null;
}

export function SourcesScrollArea({
  children,
  isLoaded,
  isLoading,
  offset,
  limit,
  setOffset,
  nextUrl,
}: SourcesScrollAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !setOffset || !limit) return;

    const onScroll = () => {
      if (isLoading || !nextUrl) return;
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      // Trigger next page when within ~1 viewport from the bottom.
      if (distanceFromBottom < el.clientHeight) {
        setOffset((offset ?? 0) + limit);
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isLoading, nextUrl, setOffset, offset, limit]);

  return (
    <Box flex="1" minHeight="0" position="relative" overflow="hidden">
      <Box
        ref={scrollRef}
        position="absolute"
        inset="0"
        overflowY="auto"
        css={{ scrollbarGutter: 'stable' }}
      >
        {children}
        {isLoading && (
          <Flex justify="center" align="center" py={4}>
            <Spinner size="md" />
          </Flex>
        )}
        {isLoaded && !isLoading && !nextUrl && (
          <Flex justify="center" py={4}>
            <Text fontSize="sm" color="text.secondary">
              {/* End-of-list marker; intentionally empty for parity with AIM. */}
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
